import Vue from "vue";
import Vuex from "vuex";
import { SquidexCommunication } from "../squidex";
import { getID } from "../utilities";

const { query } = require("./modules/graphQLQuery");
const VideoMetadata = require("./modules/VideoMetadata").default;

interface FilterInterface {
  question: string;
  generation: string;
  discipline: string;
  gender: string;
}

Vue.use(Vuex);
const Squidex = new SquidexCommunication();

const getDefaultState = () => {
  return {
    // remove dummy data in production
    questionFilters: Array<string>(),
    disciplineFilters: Array<string>(),
    generationFilters: Array<string>(),
    genderFilters: Array<string>(),
    active: {
      question: "",
      generation: "",
      discipline: "",
      gender: "",
    },
    video: undefined,
    activeNavigation: 0, // default 0 is "all" or no filters, the normal view
    filters: {
      topic: false,
      generation: false,
      discipline: false,
      gender: false,
    },
    sortedBy: "",
    searchQuery: "",
    videos: Array<any>(),
    isMobile: false,
    about: "",
    termsofuse: "",
    /* Overlay */
    overlay: {
      active: false,
      title: "",
      content: "",
    },
    loader: false,
    /* tmp state */
    activeState: {
      question: "",
      generation: "",
      discipline: "",
      gender: "",
    },
    activeNavigationState: -1,
  };
};

const state = getDefaultState();
let tmpState = {};

export default new Vuex.Store({
  state,
  getters: {
    getAbout: (state) => state.about,
    getTermsOfUse: (state) => state.termsofuse,
    getQuestionFilters: (state) => state.questionFilters,
    getDisciplineFilters: (state) => state.disciplineFilters,
    getGenerationFilters: (state) => state.generationFilters,
    getGenderFilters: (state) => state.genderFilters,
    getActiveNavigation: (state) => state.activeNavigation,
    getOverlay: (state) => state.overlay,
    getVideos: (state) => {
      const videos = state.videos;
      const filter = (key: keyof FilterInterface) =>
        videos.filter((v) => v[key] === state.active[key]);

      if (state.filters.topic && state.active.question)
        return filter("question");
      if (state.filters.generation && state.active.generation)
        return filter("generation");
      if (state.filters.gender && state.active.gender) return filter("gender");
      if (state.filters.discipline && state.active.discipline)
        return videos.filter(
          (v) => v["discipline"].indexOf(state.active["discipline"]) > -1
        );

      return videos;
    },
    getIsMobile: (state) => state.isMobile,
    getVideo: (state) => state.video,
    getLoader: (state) => state.loader,
  },
  mutations: {
    setLoader(state, loader) {
      state.loader = loader;
    },
    setAbout(state, about) {
      state.about = about;
    },
    setTermsOfUse(state, termofuse) {
      state.termsofuse = termofuse;
    },
    setOverlay(state, overlay) {
      Object.assign(state.overlay, overlay);
    },
    setVideo(state, video) {
      state.video = video;
    },
    setIsMobile(state, bool) {
      state.isMobile = bool;
    },
    addQuestion(state, question: string): void {
      state.questionFilters.push(question);
    },
    addDiscipline(state, discipline: string): void {
      state.disciplineFilters.push(discipline);
    },
    addGeneration(state, generation: string): void {
      state.generationFilters.push(generation);
    },
    addGender(state, gender: string): void {
      state.genderFilters.push(gender);
    },
    addVideo(state, video): void {
      state.videos.push(video);
    },
    setActiveNavigation(state, index: number) {
      state.activeNavigation = index;
      state.filters.topic = index === 2;
      state.filters.generation = index === 3;
      state.filters.gender = index === 4;
      state.filters.discipline = index === 5;
      /* Reset filter when navigating */
      const initialState = getDefaultState().active;
      Object.assign(state.active, initialState);
    },
    setSearchQuery(state, str: string) {
      state.searchQuery = str;
    },
    resetStore(state, store) {
      state = store;
    },
    activeFilter(state, index: number) {
      switch (state.activeNavigation) {
        case 2:
          state.active.question =
            index > -1 ? state.questionFilters[index] : "";
          break;
        case 3:
          state.active.generation =
            index > -1 ? state.generationFilters[index] : "";
          break;
        case 4:
          state.active.gender = index > -1 ? state.genderFilters[index] : "";
          break;
        case 5:
          state.active.discipline =
            index > -1 ? state.disciplineFilters[index] : "";
          break;
        default:
          console.log("None of the cases hit!");
      }
    },
    /*   saveActiveState(state) {
      state.activeNavigationState = state.activeNavigation;
      state.activeNavigation = -1;
      Object.assign(state.activeState, state.active);
    },
    loadActiveState(state) {
      state.activeNavigation = state.activeNavigationState;
      Object.assign(state.active, state.activeState);
    }, */
  },
  actions: {
    setVideo({ commit }, id) {
      return new Promise((resolve, reject) => {
        const arr = this.getters.getVideos;
        if (arr?.length > 0 && id) {
          const video = arr.find((v: any) => getID(v.video.url) == id);
          if (video) {
            commit("setVideo", video);
            resolve();
          } else reject();
        } else reject();
      });
    },

    fetchData({ commit }) {
      commit("setLoader", true);
      commit("resetStore", getDefaultState()); // Reset the store and fetch data
      /*  Squidex.fetchToken(
        "https://cms.engagelab.uiocloud.no/identity-server/connect/token",
        false
      ).then(token => {
        console.log(token);
      }); */

      Squidex.fetchGraphQL(
        "https://cms.engagelab.uiocloud.no/api/content/educational-futures/graphql",
        query
      ).then((data: any) => {
        const videos = data.queryVideosContents;
        const questions = data.queryQuestionsContents;
        const disciplines = data.queryDisciplinesContents;
        const generations = data.queryGenerationsContents;
        const genders = data.queryGendersContents;
        const about = data.queryAboutContents;
        const terms = data.queryTermsofuseContents;

        /* Populate store */
        videos.map((item: any) =>
          commit("addVideo", new VideoMetadata(item.flatData))
        );
        questions.map((item: any) =>
          commit("addQuestion", item.flatData.question)
        );
        disciplines.map((item: any) =>
          commit("addDiscipline", item.flatData.discipline)
        );
        generations.map((item: any) =>
          commit("addGeneration", item.flatData.generation)
        );
        genders.map((item: any) => commit("addGender", item.flatData.gender));

        commit("setAbout", about[0].flatData.abouttext);
        commit("setTermsOfUse", terms[0].flatData.termsofuse);
        commit("setLoader", false);
      });
    },
  },
  modules: {},
});
